/**
 * Product Manager Module
 * Handles add-to-cart functionality and product button states
 */

import { getStorage, setStorage } from '../core/storage.js';
import { getAllElements, dispatchCartUpdatedEvent } from '../core/utils.js';

export class ProductManager {
    constructor(system) {
        this.system = system;
        this.productContainers = null;
    }

    init() {
        this.productContainers = getAllElements('.section-cart-product .product-info');
        
        // Early return if no product elements found - this page doesn't have products
        if (this.productContainers.length === 0) {
            console.log('ProductManager: No product elements found, skipping initialization');
            return;
        }

        console.log('ProductManager: Initializing product functionality');
        this.updateButtons();
        this.setupAddToCartButtons();

        // Reset button state when a product is removed from the cart
        // (e.g. via the inline cart panel)
        document.addEventListener('cartUpdated', () => {
            this.updateButtons();
        });
    }

    setupAddToCartButtons() {
        // Event delegation för bättre prestanda
        document.addEventListener('click', (e) => {
            if (e.target.classList.contains('js-add-to-cart')) {
                e.preventDefault();
                this.handleAddToCart(e.target);
            }
        });
    }

    handleAddToCart(button) {
        const productElement = button.closest('.section-cart-product .product-info');
        if (!productElement) return;

        // Check availability before adding to cart
        const isAvailable = productElement.querySelector('.js-cart-available')?.textContent === 'True';
        
        if (!isAvailable) {
            console.log('Product not available - cannot add to cart');
            // Product is already disabled in template, but this is a safety check
            return;
        }

        const productId = productElement.querySelector('.js-cart-id')?.textContent;

        if (productId && !this.productIsInStorage(productId)) {
            const product = this.extractProductData(productElement);
            if (product) {
                // Include inventory data in cart item for later validation
                product.EffectiveIsAvailable = isAvailable;
                product.EffectiveStockQuantity = parseInt(productElement.querySelector('.js-cart-stock')?.textContent || '0');
                product.EffectiveTrackInventory = productElement.querySelector('.js-cart-track-inventory')?.textContent === 'True';
                product.EffectiveAllowBackorders = productElement.querySelector('.js-cart-allow-backorders')?.textContent === 'True';

                this.addProductToStorage(product);
                this.changeButton(productElement);
            }
        }

        // "Add to cart" ALWAYS opens the inline cart - also when the product is
        // already in the cart, so the customer can adjust the quantity there
        if (productId && this.system.inlineCart) {
            this.system.inlineCart.open(button);
        }
    }

    extractProductData(productElement) {
        const getData = (selector) => {
            const element = productElement.querySelector(selector);
            return element ? element.textContent.trim() : null;
        };

        const getImageUrl = () => {
            // 1. Dedicated cart image element (present on category pages;
            //    commented out in some product templates)
            const element = productElement.querySelector('.js-cart-image');
            const elementSrc = element ? element.getAttribute('src') : null;
            if (elementSrc) return elementSrc;

            // 2. Current variant's image via ProductVariantManager
            //    (product pages with variants - same logic the variant
            //    gallery itself uses)
            const variantManager = this.system.variants;
            if (variantManager && variantManager.isInitialized && variantManager.isInitialized()) {
                const variant = variantManager.getCurrentVariant();
                if (variant) {
                    const variantImages = variantManager.getVariantImages(variant);
                    if (variantImages.length > 0) {
                        return variantManager.transformImageUrl(variantImages[0]);
                    }
                }
            }

            // 3. DOM fallback: first image in the variant or standard gallery
            const galleryImage = document.querySelector(
                '.js-variant-slider img, .js-standard-gallery .gallery-item img'
            );
            return galleryImage ? galleryImage.getAttribute('src') : null;
        };

        return {
            ProductName: getData('.js-cart-name'),
            ProductNr: getData('.js-cart-id'),
            ProductPrice: getData('.js-cart-price'),
            TaxRate: getData('.js-cart-taxrate'),
            ProductUrl: getData('.js-cart-url'),
            ImageUrl: getImageUrl(),
            OrderCount: 1,
            ProductId: getData('.js-cart-productid'),  // Required for discount validation
            ProductVariantId: getData('.js-cart-productvariantid') || null,  // null for regular products
            CategoryId: getData('.js-cart-categoryid'),
            BrandId: getData('.js-cart-brandid')
        };
    }

    addProductToStorage(product) {
        const storageItems = getStorage(this.system.config.storageKey);
        storageItems.push(product);
        const success = setStorage(this.system.config.storageKey, storageItems);
        
        if (success) {
            dispatchCartUpdatedEvent(storageItems);
        }
        
        return success;
    }

    productIsInStorage(productId) {
        return getStorage(this.system.config.storageKey).some(item => item.ProductNr === productId);
    }

    updateButtons() {
        const storageItems = getStorage(this.system.config.storageKey);
        if (storageItems.length === 0) return;

        const productIds = new Set(storageItems.map(item => item.ProductNr));

        this.productContainers.forEach(productElement => {
            const productId = productElement.querySelector('.js-cart-id')?.textContent;
            if (!productId) return;

            if (productIds.has(productId)) {
                this.changeButton(productElement);
            } else {
                this.resetButton(productElement);
            }
        });
    }

    changeButton(productElement) {
        const button = productElement.querySelector('.js-add-to-cart');
        if (button) {
            // Preserve the original label so the button can be reset later
            if (!button.dataset.originalText) {
                button.dataset.originalText = button.textContent;
            }
            button.textContent = 'Tillagd i varukorg';
            // NOTE: the button stays enabled - clicking it again opens the
            // inline cart so the customer can adjust the quantity
            button.classList.add('added-to-cart');
        }
    }

    resetButton(productElement) {
        const button = productElement.querySelector('.js-add-to-cart');
        if (button && button.classList.contains('added-to-cart')) {
            button.textContent = button.dataset.originalText || 'Lägg i varukorg';
            button.disabled = false;
            button.classList.remove('added-to-cart');
        }
    }
}
